import { mapValues, chain, get, isEmpty, sum, last, first, size, round } from "lodash"
import { safeDivide } from "./math"

const DEFAULT_PRECISION = 6

export const DRIFT_STATUS = {
  VERY_LOW: "very-low",
  LOW: "low",
  NEUTRAL: "neutral",
  HIGH: "high",
  VERY_HIGH: "very-high",
}

export const TRADE_STATUS = {
  OVERSOLD_PLUS: "Extremely Oversold",
  OVERSOLD: "Oversold",
  NEUTRAL: "Neutral",
  OVERBOUGHT: "Overbought",
  OVERBOUGHT_PLUS: "Extremely Overbought",
}

export function computeDrift(mean, standardDeviation, value) {
  return safeDivide((value - mean), standardDeviation)
}

/**
 * Calculates the mean, standard deviation, and delta (change) for an array of numeric values.
 * Used to calculate drift values.
 */
export function computeDriftValues(values) {
  if (isEmpty(values)) { return null }

  const change = last(values) - first(values)
  const mean = sum(values) / values.length
  const total = sum(values.map(number => (number - mean) ** 2))
  const stdDev = size(values) > 1 ? round(Math.sqrt(total / values.length), DEFAULT_PRECISION) : null

  return {
    mean: round(mean, DEFAULT_PRECISION),
    stdDev,
    change,
  }
}

/**
 * This method returns the drift description depending on which bucket the amount falls in:
 *  - very high: 95% - 100%
 *  - high: 75% - 95%
 *  - neutral: 25% - 75%
 *  - low: 5% - 25%
 *  - very low: 0% - 5%
 */
export const getDriftType = amount => {
  const { VERY_LOW, LOW, NEUTRAL, HIGH, VERY_HIGH } = DRIFT_STATUS
  switch (true) {
    case amount < -1.644854:
      return { id: VERY_LOW, label: "Very Low" }
    case amount < -0.674490:
      return { id: LOW, label: "Low" }
    case amount < 0.674490:
      return { id: NEUTRAL, label: "Neutral" }
    case amount < 1.644854:
      return { id: HIGH, label: "High" }
    default:
      return { id: VERY_HIGH, label: "Very High" }
  }
}

/**
 * @param {*} getter Not all data arrays contain the same object structures so we need a getter function.
 *                  { date, index, factorA, factorB, factorC, ... } VS { date, index, factors: { factorA, factorB, factorC, ... }}
 * @returns { factorA: { mean, stdDev }, factorB: { mean, stdDev }, factorC: { mean, stdDev }, ... }
 *
 * The reduce line can be a bit confusing so here's what it does:
 * Given that we want to transform the following structure
 * [ { date, factorA, factorB, ... }, { date, factorA, factorB, ... }, ... ]
 * Into
 * [ { factorA: [values...], factorB: [values...], factorC: [values...] } ]
 * For each date item, we return a mapped factor object { factorA, factorB, ... } that contains the previous values and appends the current item's values
 * ------------------------------------------------------------------------------------
 * First item             Second item               Third item
 * {                      {                         {
 *  factorA: [1],   >>>     factorA: [1, 2],  >>>     factorA: [1, 2, 3],
 *  factorB: [1],   >>>     factorB: [1, 2],  >>>     factorB: [1, 2, 3],
 *  ...                     ...                       ...
 * }                      }                         }
 * ------------------------------------------------------------------------------------
 * Once that step is done we can compute the mean and standard deviation for all factors easily to have the following structure
 * { factorA: { mean, stdDev }, factorB: { mean, stDev }, ... }
 */
export function makeComputeDriftFromData(getter = (v) => v) {
  return (data) => chain(data)
    .reduce((items, item) => mapValues(getter(item), (value, key) => [...get(items, key, []), value]), {})
    .mapValues(values => {
      const mean = calculateMean(values)
      return {
        mean,
        stdDev: standardDeviation(values, mean),
      }
    })
    .value()
}

function calculateMean(numArray) {
  if (!numArray || !numArray.length) { return }
  return numArray.reduce((s, n) => s + n) / numArray.length
}

function roundTinyNumber(number) {
  if (Math.abs(number) < 0.00001) {
    return 0
  }
  return number
}

function standardDeviation(numArray, mean, usePopulation = true) {
  if (!numArray || !numArray.length) { return }
  const calculatedMean = mean || calculateMean(numArray)
  const total = numArray.reduce((acc, n) => acc + (n - calculatedMean) ** 2, 0)

  return Math.sqrt(
    roundTinyNumber(total) / (numArray.length - (usePopulation ? 0 : 1)),
  )
}

export function findTradeStatus(normalized) {
  const { OVERSOLD_PLUS, OVERSOLD, NEUTRAL, OVERBOUGHT, OVERBOUGHT_PLUS } = TRADE_STATUS

  switch (true) {
    case normalized <= -2:
      return OVERSOLD_PLUS
    case normalized <= -1:
      return OVERSOLD
    case normalized >= 2:
      return OVERBOUGHT_PLUS
    case normalized >= 1:
      return OVERBOUGHT
    default:
      return NEUTRAL
  }
}

export function getDriftIdByTradeStatus(status) {
  const { OVERSOLD_PLUS, OVERSOLD, NEUTRAL: TRADE_NEUTRAL, OVERBOUGHT, OVERBOUGHT_PLUS } = TRADE_STATUS
  const { VERY_LOW, LOW, NEUTRAL: DRIFT_NEUTRAL, HIGH, VERY_HIGH } = DRIFT_STATUS

  switch (status) {
    case OVERSOLD_PLUS:
      return VERY_LOW
    case OVERSOLD:
      return LOW
    case OVERBOUGHT:
      return HIGH
    case OVERBOUGHT_PLUS:
      return VERY_HIGH
    default:
    case TRADE_NEUTRAL:
      return DRIFT_NEUTRAL
  }
}
